const { getListArgs, getInsertProperties } = require('../tools/sql-tools')
const LATE_FEE_GRACE_PERIOD = 14
const LATE_FEE_DURATION_CAP = 45
const LATE_FEE_WEEKLY_PERCENTAGE = 0.5
const LATE_FEE_DAYS_IN_WEEK = 7
const LATE_FEE_AMOUNT_THRESHOLD = { ke: 1000, ng: 2500 }

const getPendingLateFeesQuery = ({ locationIds, writeMode = false, dueDateDifference = 0 }) => {
  const {args} = getListArgs(locationIds || [])
  let locationsFilter = ` location_id in (${args}) and `
  if (!locationIds || !locationIds.length) {
    locationsFilter = ''
  }

  const feeDurationPeriod = writeMode ? LATE_FEE_DAYS_IN_WEEK : LATE_FEE_DURATION_CAP
  const pendingLateFeeFilter = dueDateDifference && typeof dueDateDifference === 'number' ? `and lf.fee_date <= now() + interval '${dueDateDifference} days'` : ''
  const lateFeeFilter = `where l.id is null ${writeMode ? 'and lf.fee_date <= now()' : pendingLateFeeFilter} and (coalesce(lfs.late_fee_accrual, false) != false or lfs.id is null)`

  return {
    query: `
      with args as (
        select
          datetime_offset as late_fee_invoice_start_date
        from avocado.data_checkpoint where id = 'LATE_FEE_INVOICES_START_DATE'
      ),
      location_base as (
        select
          l.uuid as location_id,
          quickbooks_customer_id as customer_id,
          company_code as company,
          CASE
            WHEN l.fsid ilike '%country:ng%' then ${LATE_FEE_AMOUNT_THRESHOLD.ng}
            WHEN l.fsid ilike '%country:ke%' then ${LATE_FEE_AMOUNT_THRESHOLD.ke}
          END as amount_threshold
        from avocado.data_location l
        left join avocado.data_quickbookslocation ql on ql.location_id = l."uuid" 
        where 
          l.classification not in ('Supplier', 'Pack Point') and
          coalesce(l.late_fee_accrual, false) is true and 
          ${locationsFilter}
          lower(ql.company_code) != 'legacy'
      ),
      overdue_invoices as (
        select
          null::uuid as id,
          null::uuid as payment_plan_id,
          i.doc_no as quickbooks_doc_number,
          lb.location_id,
          i.txn_date as start_date,
          i.due_date,
          i.amount,
          i.balance,
          i.txn_id::text as txn_id,
          i.company::text as company_code,
          'invoice' as txn_type,
          lb.amount_threshold
        from location_base lb
        left join fin.invoice i on i.origin = 'qbo' and i.company::text = lb.company and i.customer_id::text = lb.customer_id
        cross join args a
        where
          i.balance > 0 and 
          i.balance > lb.amount_threshold and
          now() >= i.due_date ${writeMode ? ` + interval '${LATE_FEE_GRACE_PERIOD} days' ` : ''} and
          i.txn_date  >= a.late_fee_invoice_start_date
          -- Do not generate late fee invoice on a late fee invoice
          and not exists (
            select 1
              from avocado.data_invoice_quickbooks iq
            inner join avocado.data_invoice_line il on il.invoice_id = iq.invoice_id and il.event_type = 'fee'
            inner join avocado.late_fee lf on lf.event_id = il.event_id
            where iq.invoice_txn_id = i.txn_id::text
              and iq.company_code = i.company::text 
              and lf.id is not null
             limit 1
           )
      ),
      overdue_instalments as (
        select
          coalesce(i.id, pp.id) as id,
          pp.id as payment_plan_id,
          coalesce(i.code, inst.code) as quickbooks_doc_number,
          pp.location_id,
          pp.start_date,
          inst.due_date,
          pp.installment_amount as amount,
          inst.balance,
          null as txn_id,
          pp."company_code",
          'installment' as txn_type,
          lb.amount_threshold
        from location_base lb
        left join avocado.data_paymentplan pp on pp.location_id = lb.location_id
        left join lateral (
           select
              pp.id as payment_plan_id,
              to_char(pp.start_date + (nth - 1) * calc.freq, 'YYMMDD') as code,
              case
               when calc.installment_principal_paid > 0 then
                 pp.installment_amount - (calc.installment_principal_paid + case when coalesce(pp.separate_service_fee_and_principal, false) = true then 0 else pp.service_fee end)
                 else pp.installment_amount
              end as balance,
              cast(pp.start_date + (nth - 1) * calc.freq as date) as due_date
            from generate_series(1, pp.term) nth,
            lateral (
              select
              (pp.installment_amount*pp.term) - (pp.installment_amount*nth) as balance,
              greatest(0, least(pp.installment_amount - case when coalesce(pp.separate_service_fee_and_principal, false) = true then 0 else pp.service_fee end,
                 ( greatest( ((pp.installment_amount - case when coalesce(pp.separate_service_fee_and_principal, false) = true then 0 else pp.service_fee end) * pp.term ), pp.past_due ) - pp.quickbooks_account_balance)
                 - (nth - 1) * (pp.installment_amount - case when coalesce(pp.separate_service_fee_and_principal, false) = true then 0 else pp.service_fee end)
               )) as installment_principal_paid,
              case
                when frequency = 'WEEKLY' then interval '1 week'
                when frequency = 'BIWEEKLY' then interval '2 week'
                when frequency = 'FIFTEEN_DAYS' then interval '15 days'
                when frequency = 'MONTHLY' then interval '1 month'
                when frequency = 'FORTY_FIVE_DAYS' then interval '45 days'
                when frequency = 'BIMONTHLY' then interval '2 month'
                when frequency = 'TRIMONTHLY' then interval '3 month'
              end as freq
            ) as calc
        ) as inst ON true
        left join avocado.data_paymentplaninstallment i
           on i.payment_plan_id = pp.id and i.payment_plan_id = inst.payment_plan_id and i.due_date = inst.due_date
        cross join args a
        where
          inst.balance > 0 and 
          coalesce(pp.quickbooks_account_id, '') != '' and 
          pp.is_active is true and 
          pp.start_date::timestamptz >= a.late_fee_invoice_start_date and 
          now() >= inst.due_date ${writeMode ? ` + interval '${LATE_FEE_GRACE_PERIOD} days' ` : ''} and 
          inst.balance > lb.amount_threshold 
      ),
      overdue_transactions as (
        select * from overdue_invoices 
        union all
        select * from overdue_instalments
      ),
      generate_late_fee_lines as (
        select 
          fee_line.*
        from overdue_transactions ot
        left join lateral (
          select
            null::uuid as event_id,
            ot.location_id,
            ot.txn_type,
            ot.txn_id,
            ot.company_code,
            ot.payment_plan_id as paymentplan_id,
            ot.id as installment_id,
            coalesce(ot.balance, 0) as overdue_amount,
            ${LATE_FEE_WEEKLY_PERCENTAGE}::decimal as fee_percentage,
            (${LATE_FEE_WEEKLY_PERCENTAGE}::decimal/100::decimal) * coalesce(ot.balance, 0) as fee_amount,
            nth as fee_count,
            ((ot.due_date + interval '${LATE_FEE_GRACE_PERIOD} days') + ((nth - 1) || ' weeks')::interval )::date as fee_date,
            ot.quickbooks_doc_number
          FROM generate_series(1, (
            select
              (
              extract (day from date_trunc('day', now()) + interval '${feeDurationPeriod} days'
                -
              date_trunc('day', ot.due_date + interval '${LATE_FEE_GRACE_PERIOD} days'))::integer / ${LATE_FEE_DAYS_IN_WEEK}
              )
          )) nth
          ) fee_line on true
          where
            ((fee_line.txn_type = 'invoice' and fee_line.fee_date <= (date_trunc('day', ot.due_date) + interval '${LATE_FEE_DURATION_CAP} days'))
              or
            (fee_line.txn_type = 'installment' and fee_line.fee_date <= (date_trunc('day', ot.start_date::date) + interval '${LATE_FEE_DURATION_CAP} days')))
            and coalesce(ot.balance, 0) > ot.amount_threshold
      ),
      late_fee_installments as (
        select
          event_id,
          location_id,
          paymentplan_id,
          installment_id,
          quickbooks_doc_number,
          txn_type,
          concat(
            'Late Fee No. ', fee_count, ' for instalment - ',
            coalesce(quickbooks_doc_number, installment_id::text),
            '_', fee_date
          ) as description,
          fee_amount as amount,
          'Late Fee'::avocado.fee_item_name_type as item_name,
          fee_percentage as percentage,
          overdue_amount,
          fee_date
        from generate_late_fee_lines where txn_type = 'installment'
      ),
      late_fee_invoices as (
        select
          event_id,
          location_id,
          txn_id,
          company_code,
          quickbooks_doc_number,
          txn_type,
          concat(
            'Late Fee No. ', fee_count, ' for invoice - ',
            coalesce(quickbooks_doc_number, concat(txn_id, ' - ', company_code)::text),
            '_', fee_date
          ) as description,
          fee_amount as amount,
          'Late Fee'::avocado.fee_item_name_type as item_name,
          fee_percentage as percentage,
          overdue_amount,
          fee_date
        from generate_late_fee_lines where txn_type = 'invoice'
      ),
      diffed_installment_lines as (
        select
          lf.*
        from avocado.late_fee_installment l
        full outer join late_fee_installments lf on lf.paymentplan_id = l.paymentplan_id and lf.installment_id = l.installment_id and lf.fee_date::date = l.fee_date::date
        left join avocado.late_fee_installment_status lfs on lfs.installment_id = lf.installment_id
        ${lateFeeFilter}
      ),
      diffed_invoice_lines as (
        select
          lf.*
        from avocado.late_fee_invoice l
        full outer join late_fee_invoices lf on lf.txn_id = l.txn_id and lf.company_code = l.company_code and lf.fee_date::date = l.fee_date::date
        left join avocado.late_fee_invoice_status lfs on lfs.txn_id = lf.txn_id and lfs.company_code = lf.company_code
        ${lateFeeFilter}
      )
      select
        coalesce(
          (
            select jsonb_agg(il)
            from (
              select * from diffed_installment_lines
            ) il
          ),
          '[]'::jsonb
        ) as "instalmentLateFees",
        coalesce(
          (
            select jsonb_agg(inv)
            from (
              select * from diffed_invoice_lines
            ) inv
          ),
          '[]'::jsonb
        ) as "invoiceLateFees"
    `,
    values: Array.isArray(locationIds) ? [...locationIds] : []
  }
}

class LateFeeAdapter {
  constructor (pgConnection) {
    this.pgConnection = pgConnection
  }

  async process (locationIds) {
    const response = { lateFeeInvoiceLines: [], lateFeeInstalmentLines: [] }
    if (!locationIds || !locationIds.length) {
      return response
    }

    const {query, values} = getPendingLateFeesQuery({locationIds, writeMode: true})
    const {rows: [{ instalmentLateFees, invoiceLateFees }]} = await this.pgConnection.query(query, values)

    if (instalmentLateFees.length > 0) {
      response.lateFeeInstalmentLines = await this.createInstalmentLateFeeLines(instalmentLateFees)
    }

    if (invoiceLateFees.length > 0) {
      response.lateFeeInvoiceLines = await this.createInvoiceLateFeeLines(invoiceLateFees)
    }

    return response
  }

  async getPending (locationIds, dueDateDifference) {
    const {query, values} = getPendingLateFeesQuery({locationIds, writeMode: false, dueDateDifference})
    const {rows: [{ instalmentLateFees, invoiceLateFees }]} = await this.pgConnection.query(query, values)

    return [...instalmentLateFees, ...invoiceLateFees].map(l => ({
      location_id: l.location_id,
      paymentplan_id: l.paymentplan_id || null,
      installment_id: l.installment_id || null,
      txn_id: l.txn_id || null,
      company_code: l.company_code || null,
      description: l.description,
      txn_type: l.txn_type,
      quickbooks_doc_number: l.quickbooks_doc_number,
      amount: l.amount,
      item_name: l.item_name,
      percentage: l.percentage,
      overdue_amount: l.overdue_amount,
      fee_date: l.fee_date
    }))
  }

  async createInstalmentLateFeeLines (lines) {
    const parsedRows = lines.map(l => ({
      location_id: l.location_id,
      item_name: l.item_name,
      description: l.description,
      amount: l.amount,
      fee_date: l.fee_date,
      percentage: l.percentage,
      overdue_amount: l.overdue_amount,
      paymentplan_id: l.paymentplan_id,
      installment_id: l.installment_id
    }))

    const { columns, values, placeholders } = getInsertProperties(parsedRows)
    const {rows} = await this.pgConnection.query(`
      insert into avocado.late_fee_installment (${columns})
      values ${placeholders}
      returning event_id, seq, id, location_id, created_at, item_name, description,
        amount::float as amount, fee_date::text as fee_date, percentage::float as percentage, overdue_amount,
        paymentplan_id, installment_id, fee_invoice_txn_id
    `, values)
    return rows
  }

  async createInvoiceLateFeeLines (lines) {
    const parsedRows = lines.map(l => ({
      location_id: l.location_id,
      item_name: l.item_name,
      description: l.description,
      amount: l.amount,
      fee_date: l.fee_date,
      percentage: l.percentage,
      overdue_amount: l.overdue_amount,
      txn_id: l.txn_id,
      company_code: l.company_code
    }))

    const { columns, values, placeholders } = getInsertProperties(parsedRows)
    const {rows} = await this.pgConnection.query(`
      insert into avocado.late_fee_invoice (${columns})
      values ${placeholders}
      returning event_id, seq, id, location_id, created_at, item_name, description,
        amount::float as amount, fee_date::text as fee_date, percentage::float as percentage, overdue_amount,
        txn_id, company_code, fee_invoice_txn_id
    `, values)
    return rows
  }
}

module.exports = {
  LateFeeAdapter,
  LATE_FEE_WEEKLY_PERCENTAGE,
  LATE_FEE_GRACE_PERIOD,
  LATE_FEE_DURATION_CAP,
  LATE_FEE_DAYS_IN_WEEK
}
